import { Link } from '@mui/material';
import Button from 'components/atoms/Button';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import {
  ELicenceAuthorityPrintoutErrorLogStatus,
  ELicenceAuthorityPrintoutErrorLogType,
  ILicenceAuthority,
  ILicenceAuthorityFormValues,
  ILicenceAuthorityPrintoutErrorLog,
  ILicenceAuthorityPrintoutSettingsFormValues,
} from 'models/licenceAuthority';
import { ERouteLinks } from 'models/route';
import { ITableColumns, ITableDataSource } from 'models/table';
import React from 'react';
import { formatDate } from 'utils/date';

export const initialFormValues: ILicenceAuthorityFormValues = {
  name: '',
  isActive: '',
  authorizedPersonName: '',
  position: '',
  signatureAttachmentFile: null,
};
export const initialPrintoutSettingsFormValues: ILicenceAuthorityPrintoutSettingsFormValues = {
  username: '',
  host: '',
  port: '',
  hostFingerprints: [],
  pgpPublicKey: '',
  outboundPath: '',
  inboundPath: '',
  sendHours: [],
};

export interface ILicenceAuthoritySftpConfigInitializationResponse {
  uniqueId: string;
  sshOwnPublicKey: string;
  pgpOwnPublicKey: string;
  version: number;
}

export const formatSendHour = (hour?: number) =>
  typeof hour === 'number' ? `${hour.toString().padStart(2, '0')}:00` : undefined;

export const getSendFrequencyLabel = (
  sendHours: number[] | null | undefined,
  t: (key: string) => string,
): string | undefined => {
  if (!sendHours?.length) return undefined;

  const translationKey =
    sendHours.length > 1
      ? 'licenceAuthoritiesPrintoutSettings.firstPrintingHours.twiceADay.label'
      : 'licenceAuthoritiesPrintoutSettings.firstPrintingHours.onceADay.label';

  return t(translationKey);
};

export const mapLicenceAuthorityToDataSource = (
  data: ILicenceAuthority[],
): ITableDataSource[] =>
  data.map((item) => ({
    id: item.uniqueId,
    name: item.name,
    isActive: item.isActive,
    authorizedPersonName: item.authorizedPersonName,
    position: item.position,
  }));

interface ILicenceAuthorityTableColumns {
  t: any;
  viewAction: (id: string) => void;
  editAction: (id: string) => void;
}

export const licenceAuthorityTableColumns = ({
  t,
  viewAction,
  editAction,
}: ILicenceAuthorityTableColumns): ITableColumns[] => [
  {
    id: '4eaea220-d36f-400b-895b-231c81f091cf',
    key: 'name',
    title: 'licenceAuthorities.licenceAuthorities.label',
  },
  {
    id: '4eaea411-d36f-400b-895b-231c81f091cf',
    key: 'isActive',
    title: 'licenceAuthorities.status.label',
    align: 'center',
    isCenterTableHead: true,
    width: '140px',
    render: (_data, props) => (
      <Chips
        text={props.isActive ? t('general.active.label') : t('general.inactive.label')}
        variant={props.isActive ? EChipsVariants.success : EChipsVariants.error}
        fullWidth
      />
    ),
  },
  {
    id: '4eaea416-d36f-400b-895b-231c81f091cf',
    key: '',
    title: 'general.actions.button',
    isSortable: false,
    width: '120px',
    align: 'center',
    isCenterTableHead: true,
    render: (_data, props) => (
      <>
        <Button
          id={`licenceAuthorities_view-button-${props.id}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.view}
          onClick={() => viewAction(props.id)}
        />
        <Button
          id={`licenceAuthorities_edit-button-${props.id}`}
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.edit}
          onClick={() => editAction(props.id)}
        />
      </>
    ),
  },
];

interface ILicenceAuthorityPrintoutErrorLogColumns {
  longDateFormat: string;
  t: (key: string) => string;
  resolveAction: (id: string) => void;
}

export const mapLicenceAuthorityPrintoutErrorLogDataToDataSource = (
  data: ILicenceAuthorityPrintoutErrorLog[],
): ITableDataSource[] =>
  data.map((entry) => ({
    id: entry.uniqueId,
    type: entry.type,
    messageKey: entry.messageKey,
    detail: entry.detail,
    status: entry.status,
    firstSeen: entry.firstSeen,
    lastSeen: entry.lastSeen,
    occurence: entry.occurrenceCount,
    batchUniqueId: entry.batchUniqueId,
    batchName: entry.batchName,
  }));

export const licenceAuthorityPrintoutErrorLogColumns = ({
  longDateFormat,
  t,
  resolveAction,
}: ILicenceAuthorityPrintoutErrorLogColumns): ITableColumns[] => [
  {
    id: 'cc608c6c-aa02-4d44-9aba-6a7c23915941',
    key: 'type',
    title: 'licenceAuthorities.printoutErrorLog.table.header.type.column',
    isSortable: false,
  },
  {
    id: '0aa9dca1-2ec2-4877-80d2-433a2cf221d3',
    key: 'messageKey',
    title: 'licenceAuthorities.printoutErrorLog.table.header.messageKey.column',
    isSortable: false,
    minWidth: '200px',
    render: (_data, props) => (props.messageKey ? t(props.messageKey) : ''),
  },
  {
    id: '9140e056-d605-47e6-bb23-7f6e48840975',
    key: 'batchName',
    title: 'licenceAuthorities.printoutErrorLog.table.header.batchName.column',
    isSortable: false,
    render: (_data, props) =>
      props.batchUniqueId ? (
        <Link
          href={ERouteLinks.PrintoutBatchDetails.replace(':id', props.batchUniqueId)}
          underline="none"
          target="_blank"
          rel="noopener noreferrer"
        >
          {props.batchName}
        </Link>
      ) : (
        emptyFieldHolder(props.batchName)
      ),
  },
  {
    id: '2c6f536b-8818-449b-a5b4-8991d86e7691',
    key: 'detail',
    title: 'licenceAuthorities.printoutErrorLog.table.header.detail.column',
    isSortable: false,
  },
  {
    id: '5062b560-be54-4d87-bb14-1559a08df38a',
    key: 'status',
    title: 'licenceAuthorities.printoutErrorLog.table.header.status.column',
    isCenterTableHead: true,
    justifyContentHead: 'center',
    isSortable: false,
    render: (_data, props) =>
      props.status === ELicenceAuthorityPrintoutErrorLogStatus.ACTIVE ? (
        <Chips
          text={t('licenceAuthorities.printoutErrorLog.table.header.status.active.column')}
          variant={EChipsVariants.error}
          fullWidth
        />
      ) : (
        <Chips
          text={t('licenceAuthorities.printoutErrorLog.table.header.status.resolved.column')}
          variant={EChipsVariants.success}
          fullWidth
        />
      ),
  },
  {
    id: '0edb737e-dd00-4194-a956-88bf9c14fd09',
    key: 'firstSeen',
    title: 'licenceAuthorities.printoutErrorLog.table.header.firstSeen.column',
    isSortable: false,
    render: (_data, props) => formatDate(props.firstSeen, longDateFormat),
  },
  {
    id: 'f1d260ad-4eb0-4cc5-82c4-2d55943ff983',
    key: 'lastSeen',
    title: 'licenceAuthorities.printoutErrorLog.table.header.lastSeen.column',
    isSortable: false,
    render: (_data, props) => formatDate(props.lastSeen, longDateFormat),
  },
  {
    id: 'e3071f57-59aa-4249-82df-ef0022a51857',
    key: 'occurence',
    title: 'licenceAuthorities.printoutErrorLog.table.header.occurence.column',
    isSortable: false,
  },
  {
    id: 'e3071f57-59aa-4249-82df-ef0022a51859',
    key: 'actions',
    title: 'general.actions.button',
    isSortable: false,
    align: 'center',
    width: '60px',
    render: (_data, props) => (
      <>
        {props.type === ELicenceAuthorityPrintoutErrorLogType.MANIFEST_REJECTED &&
          props.status === ELicenceAuthorityPrintoutErrorLogStatus.ACTIVE && (
            <Button
              id={`licenceAuthorities_view-button-${props.id}`}
              variant={EButtonVariants.outlined}
              tableButton
              icon={EIconTypes.amend}
              onClick={() => resolveAction(props.id)}
            />
          )}
      </>
    ),
  },
];
